import React from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../../services/userService';
import { DEFAULT_AVATAR_URL } from '../../../constants/assets';
import { Modal } from '../../UI/Modal';
import { FollowButton } from '../../UI/FollowButton';

interface FollowUser {
    id: string;
    name: string;
    handle: string;
    avatar: string;
}

interface FollowListModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: 'followers' | 'following';
    userId: string;
}

function mapUsers(list: any[]): FollowUser[] {
    return (list || []).map((u: any) => ({
        id: u.userId,
        name: u.nickname || u.userId,
        handle: u.username || u.userId,
        avatar: u.avatarUrl || DEFAULT_AVATAR_URL
    }));
}

export const FollowListModal: React.FC<FollowListModalProps> = ({ isOpen, onClose, type, userId }) => {
    const navigate = useNavigate();
    const { data: users = [], isLoading: loading } = useSWR(
        isOpen ? `${type}-${userId}` : null,
        async () => {
            if (type === 'followers') {
                const res = await userService.getFollowers(userId, { suppressAuthPrompt: true });
                return mapUsers(res.followers);
            } else {
                const res = await userService.getFollowing(userId, { suppressAuthPrompt: true });
                return mapUsers(res.following);
            }
        },
        { revalidateOnFocus: false, dedupingInterval: 10000 }
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={type === 'followers' ? '粉丝' : '关注'}
            size="sm"
        >
            <div className="min-h-[300px] max-h-[60vh] overflow-y-auto">
                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                    </div>
                ) : users.length === 0 ? (
                    <div className="flex justify-center items-center h-40 text-gray-500">
                        暂无数据
                    </div>
                ) : (
                    <div className="space-y-4">
                        {users.map((user) => (
                            <div key={user.id} className="flex items-center justify-between">
                                <div
                                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => { onClose(); navigate(`/profile/${user.id}`); }}
                                >
                                    <img
                                        src={user.avatar}
                                        alt={user.name}
                                        className="w-10 h-10 rounded-full object-cover border border-gray-100"
                                    />
                                    <div>
                                        <h4 className="font-medium text-gray-900 text-sm leading-tight">{user.name}</h4>
                                        <p className="text-xs text-gray-500">@{user.handle}</p>
                                    </div>
                                </div>
                                <FollowButton userId={user.id} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};
